import { AfterViewInit, Component, ElementRef, HostListener, Inject, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DocusignService, GenerateSigningUrlRes } from '@services/http/docusign.service';
import { UserProfile } from 'app/services/user.service';
import { Unsubscribe } from '@shared/utility/unsubscribe';
import { catchError, finalize, Observable, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-signature-popup',
  standalone: true,
  imports: [CommonModule, FlexLayoutModule, TranslateModule],
  templateUrl: './signature-popup.component.html',
  styleUrls: ['./signature-popup.component.scss']
})
export class SignaturePopupComponent extends Unsubscribe implements AfterViewInit {
  private _dialogRef: MatDialogRef<SignaturePopupComponent> = inject(MatDialogRef<SignaturePopupComponent>);
  private _docuSignService: DocusignService = inject(DocusignService);
  private sanitizer: DomSanitizer = inject(DomSanitizer);

  public data: any;

  constructor(@Inject(MAT_DIALOG_DATA) public dialogData: any) {
    super();
    this.data = this.dialogData;
  }

  @ViewChild('container') container: ElementRef;
  signature: string;

  signingUrl?: SafeResourceUrl;
  envelopeId: string;
  loading = false;
  error?: string;

  ngAfterViewInit(): void {
    setTimeout(() => {
      const rect = (this.container.nativeElement as HTMLElement).getBoundingClientRect();
    }, 100);
  }

  ngOnInit() {
    this.loading = true;
    this.error = undefined;
    const user = this.data.user as UserProfile;
    const userFullName = `${user.firstName} ${user.lastName}`;

    this.sub = this._docuSignService
      .createEnvelope(this.data.fileName, this.data.pdfFileContents, userFullName, user.email, user.clientID)
      .pipe(
        switchMap(envelopeRes => {
          if (envelopeRes.docuSignEnvelopeID) {
            this.envelopeId = envelopeRes.docuSignEnvelopeID;

            return this._docuSignService.generateSigningUrl(
              envelopeRes.docuSignEnvelopeID,
              envelopeRes.recipients[0].localRecipientID,
              `${window.location.origin}/docusign-callback`
            );
          } else {
            return new Observable(observer => {
              observer.error('Error in creating an envelope.');
            });
          }
        }),
        catchError(e => {
          this.error = e;
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe(res => {
        if (res) {
          this.signingUrl = this.sanitizer.bypassSecurityTrustResourceUrl((res as GenerateSigningUrlRes).url);
        }
      });
  }

  @HostListener('window:message', ['$event'])
  onMessage(e: MessageEvent) {
    if (e.origin !== window.location.origin) return;

    if (e.data?.type === 'DOCUSIGN_EVENT') {
      const event = e.data.event as string | undefined;

      switch (event) {
        case 'signing_complete': {
          this.loading = true;
          this.sub = this._docuSignService.getSignedDocument(this.envelopeId).subscribe(res => {
            this._dialogRef.close(res);
          });
          break;
        }
        default:
          console.log(event);
          this._dialogRef.close();
          break;
      }
    }
  }
}
